import React, { useEffect, useState, useCallback } from "react";
import { X, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/i18n";

const SEEN_KEY = "hs_promo_seen";
const DONE_KEY = "hs_promo_code";

export default function PromoPopup() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(null); // { active, discount_value, discount_type }
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState(localStorage.getItem(DONE_KEY) || "");
  const [copied, setCopied] = useState(false);

  // Récupère la config popup : n'apparaît que si une campagne est active.
  useEffect(() => {
    api.get("/promo/popup").then(({ data }) => setConfig(data)).catch(() => setConfig({ active: false }));
  }, []);

  const show = useCallback(() => {
    if (!config?.active) return;
    if (localStorage.getItem(SEEN_KEY) || document.body.dataset.promoOpen === "1") return;
    if (window.location.pathname.startsWith("/admin") || window.location.pathname.startsWith("/payer")) return;
    document.body.dataset.promoOpen = "1";
    setOpen(true);
  }, [config]);

  useEffect(() => {
    if (!config?.active) return;
    if (localStorage.getItem(SEEN_KEY)) return;
    const timer = setTimeout(show, 28000);
    const onScroll = () => {
      const p = window.scrollY / (document.body.scrollHeight - window.innerHeight + 1);
      if (p > 0.55) show();
    };
    const onLeave = (e) => { if (e.clientY <= 0) show(); };
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("mouseout", onLeave);
    return () => { clearTimeout(timer); window.removeEventListener("scroll", onScroll); document.removeEventListener("mouseout", onLeave); };
  }, [show, config]);

  const close = () => { setOpen(false); localStorage.setItem(SEEN_KEY, "1"); };

  const submit = async (e) => {
    e.preventDefault();
    const email = (form.email || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(t("promo_invalid_email"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/promo/request", { ...form, email });
      setCode(data.code);
      localStorage.setItem(DONE_KEY, data.code);
      localStorage.setItem(SEEN_KEY, "1");
      toast.success(t("promo_toast_sent"));
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = Array.isArray(d) ? t("promo_invalid_email") : (typeof d === "string" ? d : t("promo_error"));
      toast.error(msg);
    }
    finally { setBusy(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = code; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true); toast.success(t("promo_toast_copied")); setTimeout(() => setCopied(false), 2000);
  };

  if (!open) return null;

  const isAmount = config?.discount_type === "amount";
  const value = config?.discount_value ?? 10;
  const bigDiscount = isAmount ? `-${value} €` : `-${value}%`;

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="promo-popup">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-md bg-[#0F0F0F] border border-[#BC5D2E]/40 overflow-hidden" style={{ boxShadow: "0 0 80px rgba(188,93,46,0.20)" }}>
        <button onClick={close} className="absolute top-4 right-4 z-10 text-[#737373] hover:text-white" data-testid="promo-close"><X size={20} /></button>

        {/* Hero réduction */}
        <div className="relative px-8 pt-12 pb-8 text-center border-b border-[#BC5D2E]/20" style={{ background: "radial-gradient(circle at 50% 0%, rgba(188,93,46,0.18), transparent 70%)" }}>
          <span className="font-mono-hs text-[10px] uppercase tracking-[0.35em] text-[#BC5D2E]">{t("promo_welcome")}</span>
          <div className="font-clash font-bold text-7xl sm:text-8xl leading-none tracking-tighter text-[#BC5D2E] mt-3" data-testid="promo-discount">{bigDiscount}</div>
          <div className="font-clash uppercase tracking-tight text-lg text-[#F5F5F0] mt-2">{t("promo_first_order")}</div>
        </div>

        <div className="px-8 py-7">
          {code ? (
            <div data-testid="promo-result">
              <h2 className="font-clash text-xl uppercase tracking-tight mb-2">{t("promo_ready_title")}</h2>
              <p className="text-[#A3A3A3] text-sm mb-5">{t("promo_ready_desc")}</p>
              <div className="flex items-center gap-2 mb-4">
                <div className="flex-1 border border-dashed border-[#BC5D2E] py-4 text-center font-mono-hs text-2xl tracking-[0.3em] text-[#BC5D2E]" data-testid="promo-code">{code}</div>
                <button onClick={copy} className="border border-white/20 p-4 hover:border-white text-[#F5F5F0]" data-testid="promo-copy">{copied ? <Check size={20} className="text-[#4ade80]" /> : <Copy size={20} />}</button>
              </div>
              <button onClick={close} className="w-full border border-white/20 py-3 uppercase tracking-widest text-xs hover:border-white text-[#F5F5F0]">{t("promo_close")}</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="promo-form">
              <p className="text-[#A3A3A3] text-sm mb-5">{t("promo_form_a")}<strong className="text-[#F5F5F0]">{t("promo_form_strong")}</strong>{t("promo_form_b")}</p>
              <input required placeholder={t("promo_firstname")} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="hs-input mb-3" data-testid="promo-firstname" />
              <input required type="email" placeholder={t("promo_email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="hs-input mb-5" data-testid="promo-email" />
              <button type="submit" disabled={busy} className="w-full bg-[#BC5D2E] text-white py-4 uppercase tracking-widest text-sm hover:bg-[#8A281E] transition-colors disabled:opacity-50" data-testid="promo-submit">{busy ? "..." : t("promo_get_code")}</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
